import * as React from 'react';

import {BulletedList} from '../../bulleted-list/bulleted-list';
import {Card} from '../../card/card';
import {SegmentedControl} from '../../card/segmented-control';
import {colors} from '../../colors/colors';
import {Stack} from '../../layout/stack/stack';
import {NumberInput} from '../../number-input/number-input';
import {Switch} from '../../switch/switch';
import {Body} from '../../typography/body';

import {LearnMore} from '../learn-more';
import {PlanPrice} from '../plan-price';
import type {Plan, SelectedAddOn, Product} from '../types';

import {AddOnPickerSlider} from './add-on-picker-card-slider';
import {getPlanToDisplay, getProductPill, CUSTOM_PLAN_CODE} from './helpers';

import styles from './add-on-picker-card.module.css';

type Props = {
    plans: Plan[];
    userQuantityTerm?: string;
    isUserQuantityBased: boolean;
    isCycleBased: boolean;
    selectedQuantity: number | null | undefined;
    isEnabled: boolean;
    isIncluded: boolean;
    defaultPlanCode: string | null | undefined;
    selectedPlanCode: string | null | undefined;
    infoMessage?: React.ReactNode;
    errorMessage?: React.ReactNode;
    setSelectedAddOns: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void;
    onSelectedPlanChange: (
        product: Product,
        planCode: string,
        setSelectedAddOns: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void,
        quantity?: number
    ) => void;
    onIsEnabledChange: () => void;
    onOpenModal: () => void;
    cardWidth?: string;
};

export function AddOnPickerCard(props: Props) {
    const planToDisplay = getPlanToDisplay(
        props.plans,
        props.selectedPlanCode,
        props.defaultPlanCode
    );
    if (!planToDisplay) {
        return null;
    }

    const product = planToDisplay.product;
    const hasMultiplePlans = props.plans.length > 1;
    // @ts-expect-error
    const startsAtPrice = props.plans[0].price;
    const shouldShowStartsAtPrice = hasMultiplePlans && !props.isEnabled && !props.isCycleBased;
    const shouldIncludeDollarSign = planToDisplay.code !== CUSTOM_PLAN_CODE;
    const price =
        typeof props.selectedQuantity === 'number' && props.isUserQuantityBased
            ? props.selectedQuantity * Number.parseInt(planToDisplay.price)
            : planToDisplay.price;
    const stickerPrice =
        typeof props.selectedQuantity === 'number' && props.isUserQuantityBased
            ? props.selectedQuantity * Number.parseInt(planToDisplay.stickerPrice)
            : planToDisplay.stickerPrice;

    const handleQuantityChange = (quantity: number) => {
        props.onSelectedPlanChange(product, planToDisplay.code, props.setSelectedAddOns, quantity);
    };

    const monthlyPlan = props.plans.find((plan) => plan.cycleText === 'MONTHLY');
    const annualPlan = props.plans.find((plan) => plan.cycleText === 'ANNUALLY');

    const showCycleControl =
        props.isCycleBased && hasMultiplePlans && monthlyPlan && annualPlan && !props.isIncluded;

    let planPrice = 0;
    if (props.isIncluded) {
        // @ts-expect-error
        planPrice = '0';
    } else if (shouldShowStartsAtPrice) {
        // @ts-expect-error
        planPrice = startsAtPrice;
    } else {
        // @ts-expect-error
        planPrice = `${price}`;
    }

    let stickerPricDisplay = 0;
    if (props.isIncluded) {
        // @ts-expect-error
        stickerPricDisplay = '0';
    } else if (shouldShowStartsAtPrice) {
        // @ts-expect-error
        stickerPricDisplay = startsAtPrice;
    } else {
        // @ts-expect-error
        stickerPricDisplay = `${stickerPrice}`;
    }

    const priceDisplay = (
        <Stack spacing={2}>
            <PlanPrice
                // @ts-expect-error
                price={planPrice}
                // @ts-expect-error
                stickerPrice={stickerPricDisplay}
                justify={planToDisplay.description ? 'end' : 'start'}
                includePlus={shouldShowStartsAtPrice}
                includeDollarSign={shouldIncludeDollarSign}
            />
            <Body color='dark' size='small'>
                {props.isIncluded ? 'Included' : 'per month'}
            </Body>
        </Stack>
    );

    return (
        <div>
            <Card
                hasBorder={true}
                hasShadow={true}
                fitHeight={true}
                fitContent={true}
                borderColor={props.isEnabled ? colors.blue : undefined}
                overflowVisible={true}
            >
                <div
                    style={{width: props.cardWidth ? props.cardWidth : '800px'}}
                    className={styles.container}
                >
                    <div className={styles.pill}>{getProductPill(planToDisplay.product)}</div>
                    <div className='flex gap-8 justify-sb'>
                        <div className='flex flex-dir-col gap-24'>
                            {!planToDisplay.description && <>{priceDisplay}</>}
                            {planToDisplay.description && (
                                <Body isBold={true}>{planToDisplay.description}</Body>
                            )}
                            <BulletedList
                                size='small'
                                bullets={planToDisplay.planHighlights.map((highlight) => (
                                    <Body key={highlight}>{highlight}</Body>
                                ))}
                                marginBottom={0}
                            />
                        </div>
                        {/* 220px is the width of the segmentedControl, 115px is the width of the switch */}
                        <div
                            className='flex flex-dir-col align-end gap-8'
                            style={{
                                minWidth: showCycleControl ? '220px' : '115px',
                            }}
                        >
                            {showCycleControl && monthlyPlan && annualPlan && (
                                <SegmentedControl
                                    buttonOptions={[
                                        {
                                            value: monthlyPlan.code,
                                            content: (
                                                <div className='py-4 px-16 flex justify-center'>
                                                    Pay monthly
                                                </div>
                                            ),
                                        },
                                        {
                                            value: annualPlan.code,
                                            content: (
                                                <div className='py-4 px-16 flex justify-center'>
                                                    Pay annually
                                                </div>
                                            ),
                                        },
                                    ]}
                                    isDisabled={!props.isEnabled}
                                    selectedValue={planToDisplay.code}
                                    setSelectedValue={(planCode) =>
                                        props.onSelectedPlanChange(
                                            product,
                                            planCode,
                                            props.setSelectedAddOns
                                        )
                                    }
                                />
                            )}
                            {planToDisplay.description && <>{priceDisplay}</>}
                            <div className={styles.switch}>
                                <Switch
                                    isDisabled={props.isIncluded}
                                    isOn={props.isEnabled}
                                    onChange={props.onIsEnabledChange}
                                />
                            </div>
                            {props.isEnabled &&
                                props.isUserQuantityBased &&
                                props.selectedQuantity && (
                                    <div className='flex justify-sb align-center gap-8'>
                                        <Body size='small' color='light'>
                                            {props.userQuantityTerm} users
                                        </Body>
                                        <NumberInput
                                            value={props.selectedQuantity}
                                            onChange={handleQuantityChange}
                                            hasError={Boolean(props.errorMessage)}
                                            // @ts-expect-error
                                            min='1'
                                        />
                                    </div>
                                )}
                        </div>
                    </div>
                    {props.isEnabled && hasMultiplePlans && !props.isCycleBased && (
                        <AddOnPickerSlider
                            plans={props.plans}
                            selectedPlan={planToDisplay}
                            onSelectedPlanChange={(planCode) =>
                                props.onSelectedPlanChange(
                                    product,
                                    planCode,
                                    props.setSelectedAddOns
                                )
                            }
                        />
                    )}
                    <div className='flex justify-sb mt-8'>
                        <LearnMore
                            product={planToDisplay.product}
                            onOpenModal={props.onOpenModal}
                        />
                        {(props.infoMessage || props.errorMessage) && (
                            <div className='flex flex-dir-col align-end'>
                                {props.infoMessage}
                                {props.errorMessage}
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
}
